import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
    Avatar,
    Box,
    Divider,
    Drawer,
    Fab,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
    useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AddIcon from '@mui/icons-material/Add';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { Link, NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { ReferralUrlCapture } from '../../components/ReferralCapture/ReferralUrlCapture';
import { logout } from '../../redux/authentication/action';
import { useDispatch, useSelector } from 'react-redux';
import { currentSystemForm, showBrandGuideSelector } from '../../selectors/selectors';
import { setSystemForm } from '../../redux/appConfigurator/action';
import { GenericFormFiller } from '../../components/Feed/GenericFormFiller';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { userProfileSelector } from '../Dashboard/selectors';
import { getHostDashboard, setBrandDashboardActiveBrand, updateBrand } from '../../redux/buzzbridge/actions';
import { fetchCampaignHostMine } from '../../redux/campaignHostSubscription/actions';
import {
    activeBrandDashboardBrandSelector,
    brandsSelector,
    gettingHostDashboardSelector,
    hostDashboardErrorSelector,
    hostDashboardSelector,
} from '../../redux/buzzbridge/selectors';
import type { IBrand } from '../../redux/buzzbridge/types';
import {
    DashboardViewOverlay,
    DashboardViewTrigger,
} from '../../components/DashboardViewSwitcher/DashboardViewSwitcher';
import { BrandNotificationsBell } from './BrandNotificationsBell';
import {
    BRAND_LUXE_BG,
    BRAND_LUXE_GOLD,
    BRAND_LUXE_MUTED,
    BRAND_LUXE_PRIMARY,
    brandMeshBackground,
} from './brandDashboardTokens';
import {
    brandOpsCanManageWalletSelector,
    brandOpsFetchingMyMembershipsSelector,
    brandOpsMyMembershipsFetchedSelector,
} from '../../redux/brandOps/selectors';
import { fetchBrandOpsAccess, fetchMyBrandMemberships } from '../../redux/brandOps/actions';
import { globalConfigurationSelector } from '../../selectors/selectors';
import { BRAND_SAMPLE_IMAGES } from './brandDashboardMockAssets';
import { BrandOnboardingModal } from './BrandOnboardingModal';
import { DashboardWorkspaceRedirect } from '../../components/DashboardWorkspaceRedirect';
import { FloatingMessageButton } from '../../components/FloatingChat/FloatingMessageButton';
import { BrandDashboardMediaOverlays } from './BrandDashboardMediaOverlays';
import { useBrandGlobalConfiguration } from '../../hook/useBrandGlobalConfiguration';
import { connect } from '../../redux/live/action';
import { getJWtDetails } from '../../utils/AppUtils';
import { resourceTeamsUrl } from '../../configurations/api/url';
import {
    SMALL_MOBILE_MEDIA_QUERY,
    FLOATING_MESSAGES_MOBILE_HEADER_SLOT_ID,
} from '../../components/FloatingChat/floatingMessageUiConstants';
import { Tutorial } from '../../components/Tutorial/Tutorial';
import { brandTutorialSteps } from '../../components/EsSettings/settings.data';

/** Clicks on these elements open the brand gate (onboarding) instead of navigating away. */
const BRAND_PROFILE_GATE_INTERACTIVE_SELECTOR =
    'a[href],button,input,select,textarea,[role="button"],[role="menuitem"],[role="link"],[role="tab"],label,.MuiButtonBase-root';

type BrandNavItem = {
    to: string;
    label: string;
    shortLabel: string;
    end: boolean;
    Icon: typeof DashboardOutlinedIcon;
    /** Hide for managers without wallet/payroll access */
    requiresWalletAccess?: boolean;
    /** Hide when features.social_manager is inactive (super-admins always see it) */
    requiresSocialManager?: boolean;
};

const NAV_BASE: BrandNavItem[] = [
    { to: ROUTES.BRAND, label: 'Dashboard', shortLabel: 'Dashboard', end: true, Icon: DashboardOutlinedIcon },
    {
        to: ROUTES.BRAND_CAMPAIGNS,
        label: 'Campaigns',
        shortLabel: 'Campaigns',
        end: false,
        Icon: AutoAwesomeMotionOutlinedIcon,
    },
    {
        to: ROUTES.BRAND_FIND_TALENT,
        label: 'Find talent',
        shortLabel: 'Talent',
        end: false,
        Icon: PersonSearchOutlinedIcon,
    },
    {
        to: ROUTES.BRAND_MANAGE,
        label: 'Social Manager',
        shortLabel: 'Social',
        end: false,
        Icon: ManageAccountsOutlinedIcon,
        requiresSocialManager: true,
    },
    {
        to: ROUTES.BRAND_WALLET,
        label: 'Wallet',
        shortLabel: 'Wallet',
        end: false,
        Icon: AccountBalanceWalletOutlinedIcon,
        requiresWalletAccess: true,
    },
];

export default function BrandDashboardLayout() {
    const dispatch = useDispatch();
    const { device } = useThemeContext();
    const user = useSelector(userProfileSelector);
    const brands = useSelector(brandsSelector);
    const activeBrand = useSelector(activeBrandDashboardBrandSelector);
    const canManageWallet = useSelector(brandOpsCanManageWalletSelector);
    const fetchingMyMemberships = useSelector(brandOpsFetchingMyMembershipsSelector);
    const myMembershipsFetched = useSelector(brandOpsMyMembershipsFetchedSelector);
    const hostDashboard = useSelector(hostDashboardSelector);
    const gettingDashboard = useSelector(gettingHostDashboardSelector);
    const dashboardError = useSelector(hostDashboardErrorSelector);
    const globalConfig = useSelector(globalConfigurationSelector);
    const isSuperAdmin = user?.role === 'super-admin';
    const socialManagerActive = globalConfig?.features?.social_manager !== 'inactive';
    const NAV = NAV_BASE.filter(item => {
        if (item.requiresWalletAccess && !canManageWallet) return false;
        if (item.requiresSocialManager && !socialManagerActive && !isSuperAdmin) return false;
        return true;
    });
    const [switchOpen, setSwitchOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [onboardingOpen, setOnboardingOpen] = useState(false);
    const [onboardingMode, setOnboardingMode] = useState<'create' | 'edit'>('edit');
    const showBrandTour = useSelector(showBrandGuideSelector);
    const [brandMenuAnchor, setBrandMenuAnchor] = useState<null | HTMLElement>(null);
    const location = useLocation();
    const nav = useNavigate();
    const selectedSystemForm = useSelector(currentSystemForm);
    const isSmallMobile = useMediaQuery(SMALL_MOBILE_MEDIA_QUERY);
    const isTabletMessageWindow = useMediaQuery('(max-width:767px) and (min-width:415px)');
    const bg = brandMeshBackground(BRAND_LUXE_BG);
    const avatarSrc = user?.profile_picture || BRAND_SAMPLE_IMAGES.headerAvatar;
    const brandCampaignParticipantMatch = useMatch({ path: ROUTES.BRAND_CAMPAIGN_PARTICIPANT, end: true });

    useBrandGlobalConfiguration();

    const { isSignedIn, token } = getJWtDetails();

    // Whether the user is an accepted member of any campaign/contest/audition team.
    // If true they have a brand context via the invite and should NOT see brand creation.
    const [hasResourceMembership, setHasResourceMembership] = useState(false);
    const [resourceMembershipChecked, setResourceMembershipChecked] = useState(false);
    const resourceMembershipFetched = useRef(false);

    useLayoutEffect(() => {
        if (isSignedIn) dispatch(connect());
    }, [dispatch, isSignedIn]);

    useEffect(() => {
        if (!gettingDashboard) {
            dispatch(getHostDashboard({}));
        }
        dispatch(fetchCampaignHostMine());
        // Fetch brands the user is a team member of (non-owner managers).
        // Must run at this level so the result is ready before the onboarding gate fires.
        dispatch(fetchMyBrandMemberships());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (resourceMembershipFetched.current || !token) return;
        resourceMembershipFetched.current = true;
        axios
            .get(`${resourceTeamsUrl}/mine`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => {
                const rows: Array<{ status: string }> = res.data?.data || [];
                setHasResourceMembership(rows.some(r => r.status === 'accepted'));
            })
            .catch(() => {})
            .finally(() => setResourceMembershipChecked(true));
    }, [token]);

    useEffect(() => {
        if (activeBrand?._id) {
            dispatch(fetchBrandOpsAccess({ brandId: activeBrand._id }));
        }
    }, [dispatch, activeBrand?._id]);

    // Suppress the brand creation gate when:
    // (a) team_invite param is present — invite is still being processed, or
    // (b) user is an accepted campaign/contest/audition team member (resource team)
    const hasTeamInviteParam = new URLSearchParams(location.search).has('team_invite');
    const suppressBrandGate = hasTeamInviteParam || hasResourceMembership;

    useEffect(() => {
        // Wait for all checks to complete before deciding the user truly has no brand context.
        if (
            hostDashboard &&
            !gettingDashboard &&
            !dashboardError &&
            brands.length === 0 &&
            !activeBrand &&
            myMembershipsFetched &&
            !fetchingMyMemberships &&
            resourceMembershipChecked &&
            !suppressBrandGate
        ) {
            setOnboardingMode('create');
            setOnboardingOpen(true);
        }
    }, [
        hostDashboard,
        gettingDashboard,
        dashboardError,
        brands,
        activeBrand,
        myMembershipsFetched,
        fetchingMyMemberships,
        resourceMembershipChecked,
        suppressBrandGate,
    ]);

    const needsBrandProfile =
        Boolean(hostDashboard) &&
        !gettingDashboard &&
        !dashboardError &&
        brands.length === 0 &&
        !activeBrand &&
        myMembershipsFetched &&
        !fetchingMyMemberships &&
        resourceMembershipChecked &&
        !suppressBrandGate;
    const brandProfileGateActive = needsBrandProfile && !onboardingOpen;

    useEffect(() => {
        if (!brandProfileGateActive) return undefined;
        const onClickCapture = (e: MouseEvent) => {
            if (e.button !== 0) return;
            const t = e.target;
            if (!(t instanceof Element)) return;
            if (t.closest('[data-skip-brand-profile-gate="true"]')) return;
            const interactive = t.closest(BRAND_PROFILE_GATE_INTERACTIVE_SELECTOR);
            if (!interactive) return;
            if (interactive instanceof HTMLButtonElement && interactive.disabled) return;
            if (interactive instanceof HTMLInputElement && interactive.disabled) return;
            if (
                interactive instanceof HTMLAnchorElement &&
                (!interactive.getAttribute('href') || interactive.href === '')
            )
                return;
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            setOnboardingMode('create');
            setOnboardingOpen(true);
        };
        document.addEventListener('click', onClickCapture, true);
        return () => document.removeEventListener('click', onClickCapture, true);
    }, [brandProfileGateActive]);

    const handleFormsOnClose = () => {
        dispatch(setSystemForm(undefined));
    };

    const headerBrandLabel =
        activeBrand?.name ??
        (gettingDashboard ? '…' : dashboardError ? 'Brand' : brands.length === 0 ? 'Add your brand' : 'Your brand');

    const openCreateBrand = () => {
        setBrandMenuAnchor(null);
        setOnboardingMode('create');
        setOnboardingOpen(true);
    };

    const openEditBrand = () => {
        setBrandMenuAnchor(null);
        setOnboardingMode('edit');
        setOnboardingOpen(true);
    };

    const selectBrand = (brand: IBrand) => {
        dispatch(setBrandDashboardActiveBrand(brand));
        dispatch(getHostDashboard({ brandId: brand._id }));
        setBrandMenuAnchor(null);
        if (brand._id && !brand.is_default) {
            dispatch(
                updateBrand({
                    documentId: brand._id,
                    payload: { is_default: true },
                }),
            );
        }
    };

    const openBrandHeader = (event: React.MouseEvent<HTMLElement>) => {
        if (brands.length === 0) {
            openCreateBrand();
            return;
        }
        setBrandMenuAnchor(event.currentTarget);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                ...bg,
                color: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <ReferralUrlCapture />
            <Box
                component="header"
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: { xs: 2, md: 3 },
                    py: 2,
                    bgcolor: 'rgba(14,14,14,0.8)',
                    backdropFilter: 'blur(16px)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        aria-label="Menu"
                        size="small"
                        onClick={() => setDrawerOpen(true)}
                        sx={{ color: BRAND_LUXE_GOLD }}>
                        <MenuIcon />
                    </IconButton>
                    <Box
                        component="button"
                        type="button"
                        onClick={openBrandHeader}
                        sx={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}>
                        {activeBrand?.logo ? (
                            <Box
                                component="img"
                                src={activeBrand.logo}
                                alt=""
                                sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'contain', flexShrink: 0 }}
                            />
                        ) : null}
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: '1.35rem', md: '1.5rem' },
                                fontWeight: 900,
                                fontStyle: 'italic',
                                letterSpacing: '-0.06em',
                                color: BRAND_LUXE_GOLD,
                            }}>
                            {headerBrandLabel}
                        </Typography>
                        <KeyboardArrowDownIcon sx={{ color: BRAND_LUXE_GOLD, fontSize: 22, flexShrink: 0 }} />
                    </Box>
                    <Menu
                        anchorEl={brandMenuAnchor}
                        open={Boolean(brandMenuAnchor)}
                        onClose={() => setBrandMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        PaperProps={{
                            sx: {
                                mt: 1,
                                minWidth: 260,
                                bgcolor: 'rgba(22,22,22,0.98)',
                                border: '1px solid rgba(255,255,255,0.08)',
                                '& .MuiMenuItem-root': {
                                    color: '#fff',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                    '&.Mui-selected': {
                                        bgcolor: 'rgba(255,201,101,0.12)',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'rgba(255,201,101,0.18)' },
                                    },
                                },
                            },
                        }}>
                        {brands.map(brand => (
                            <MenuItem
                                key={brand._id}
                                selected={brand._id === activeBrand?._id}
                                onClick={() => selectBrand(brand)}
                                sx={{ gap: 1.5, py: 1.25 }}>
                                {brand.logo ? (
                                    <Avatar src={brand.logo} alt="" variant="rounded" sx={{ width: 32, height: 32 }} />
                                ) : (
                                    <Avatar
                                        variant="rounded"
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            bgcolor: 'rgba(255,193,7,0.15)',
                                            color: BRAND_LUXE_GOLD,
                                            fontSize: 14,
                                        }}>
                                        {(brand.name || '?').slice(0, 1)}
                                    </Avatar>
                                )}
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        noWrap
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 14,
                                            color: '#fff',
                                        }}>
                                        {brand.name}
                                    </Typography>
                                    {brand.is_default ? (
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: BRAND_LUXE_PRIMARY,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            }}>
                                            Default
                                        </Typography>
                                    ) : null}
                                </Box>
                            </MenuItem>
                        ))}
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', my: 0.5 }} />
                        <MenuItem onClick={openCreateBrand} sx={{ gap: 1.5, py: 1.25, color: BRAND_LUXE_PRIMARY }}>
                            <AddIcon sx={{ fontSize: 20 }} />
                            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13 }}>
                                Create new brand
                            </Typography>
                        </MenuItem>
                        <MenuItem
                            onClick={openEditBrand}
                            sx={{
                                fontSize: 13,
                                color: BRAND_LUXE_MUTED,
                            }}>
                            Brand profile & settings
                        </MenuItem>
                    </Menu>
                </Box>

                {!device.isMobile && (
                    <Box
                        id="brand-nav"
                        sx={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, justifyContent: 'center' }}>
                        {NAV.map(({ to, label, end, Icon }) => (
                            <NavLink key={to} to={to} end={end} style={{ textDecoration: 'none' }}>
                                {({ isActive }) => (
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 14,
                                            letterSpacing: '-0.02em',
                                            color: isActive ? BRAND_LUXE_GOLD : BRAND_LUXE_MUTED,
                                            transition: 'color 0.2s',
                                            '&:hover': { color: BRAND_LUXE_GOLD },
                                        }}>
                                        {label}
                                    </Typography>
                                )}
                            </NavLink>
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <BrandNotificationsBell />
                    <DashboardViewTrigger onOpen={() => setSwitchOpen(true)} />
                    {isSmallMobile || isTabletMessageWindow ? (
                        <Box
                            id={FLOATING_MESSAGES_MOBILE_HEADER_SLOT_ID}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        />
                    ) : null}
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            border: '1px solid rgba(72,72,71,0.35)',
                            flexShrink: 0,
                            cursor: 'pointer',
                        }}>
                        <Box
                            component="img"
                            src={avatarSrc}
                            alt=""
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </Box>
                </Box>
            </Box>

            <DashboardWorkspaceRedirect />

            <Box
                component="main"
                sx={{
                    flex: 1,
                    pt: { xs: 11, md: 12 },
                    pb: { xs: 16, md: 4 },
                    px: { xs: 2, md: 3 },
                    maxWidth: 1280,
                    mx: 'auto',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Outlet key={location.pathname} />
                </Box>
            </Box>

            {device.isMobile && (
                <Box
                    component="nav"
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1200,
                        display: { xs: 'flex', md: 'none' },
                        justifyContent: 'space-around',
                        alignItems: 'center',
                        px: 2,
                        pt: 1.5,
                        pb: 3,
                        bgcolor: 'rgba(19,19,19,0.6)',
                        backdropFilter: 'blur(24px)',
                        borderRadius: '2rem 2rem 0 0',
                        boxShadow: '0 -10px 40px rgba(0,0,0,0.8)',
                    }}>
                    {NAV.map(({ to, shortLabel, end, Icon }) => (
                        <NavLink key={to} to={to} end={end} style={{ textDecoration: 'none' }}>
                            {({ isActive }) => (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        minWidth: 64,
                                        color: isActive ? BRAND_LUXE_GOLD : BRAND_LUXE_MUTED,
                                        opacity: isActive ? 1 : 0.65,
                                        transform: isActive ? 'scale(1.08)' : 'none',
                                        filter: isActive ? 'drop-shadow(0 0 8px rgba(255,193,7,0.35))' : 'none',
                                        transition: 'all 0.2s',
                                    }}>
                                    <Icon sx={{ fontSize: 24 }} />
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 10,
                                            letterSpacing: '0.12em',
                                            textTransform: 'uppercase',
                                        }}>
                                        {shortLabel}
                                    </Typography>
                                </Box>
                            )}
                        </NavLink>
                    ))}
                </Box>
            )}

            {!device.isMobile && location.pathname !== ROUTES.BRAND_CAMPAIGN_NEW && (
                <Fab
                    id="brand-create-fab"
                    component={Link}
                    to={ROUTES.BRAND_CAMPAIGN_NEW}
                    color="primary"
                    aria-label="create"
                    sx={{
                        position: 'fixed',
                        bottom: 96,
                        right: 24,
                        zIndex: 1100,
                        width: 64,
                        height: 64,
                        bgcolor: BRAND_LUXE_PRIMARY,
                        color: '#5f4200',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                        '&:hover': { bgcolor: '#feb700', transform: 'scale(1.08)' },
                    }}>
                    <AddIcon sx={{ fontSize: 32 }} />
                </Fab>
            )}

            {!brandCampaignParticipantMatch && <FloatingMessageButton forceHeaderSlotOnTablet />}

            <BrandOnboardingModal
                open={onboardingOpen}
                onClose={() => setOnboardingOpen(false)}
                initialData={onboardingMode === 'edit' ? activeBrand ?? undefined : undefined}
                onGoToWallet={() => nav(ROUTES.BRAND_WALLET)}
            />

            {selectedSystemForm ? (
                <GenericFormFiller
                    initialStateForm={selectedSystemForm}
                    onClose={handleFormsOnClose}
                    open={!!selectedSystemForm}
                    uiVariant="brand"
                />
            ) : null}

            <DashboardViewOverlay open={switchOpen} onClose={() => setSwitchOpen(false)} />

            <BrandDashboardMediaOverlays />

            {showBrandTour && <Tutorial stepsDetails={brandTutorialSteps} configField="show_brand_guide" />}

            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: { xs: '80vw', sm: 320 },
                        bgcolor: '#111111',
                        borderRight: '1px solid rgba(255,255,255,0.06)',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}>
                <Box sx={{ px: 2.5, pt: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {activeBrand?.logo ? (
                        <Box
                            component="img"
                            src={activeBrand.logo}
                            alt=""
                            sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'contain', flexShrink: 0 }}
                        />
                    ) : null}
                    <Box>
                        <Typography
                            noWrap
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 18,
                                fontWeight: 900,
                                fontStyle: 'italic',
                                letterSpacing: '-0.04em',
                                color: BRAND_LUXE_GOLD,
                            }}>
                            {headerBrandLabel}
                        </Typography>
                        {user?.email && (
                            <Typography noWrap sx={{ fontSize: 12, color: BRAND_LUXE_MUTED, mt: 0.25 }}>
                                {user.email}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                <List sx={{ flex: 1, pt: 1 }}>
                    {NAV.map(({ to, label, end, Icon }) => (
                        <NavLink key={to} to={to} end={end} style={{ textDecoration: 'none', color: 'inherit' }}>
                            {({ isActive }) => (
                                <ListItemButton
                                    selected={isActive}
                                    onClick={() => setDrawerOpen(false)}
                                    sx={{
                                        py: 1.5,
                                        px: 2.5,
                                        '&.Mui-selected': {
                                            bgcolor: 'rgba(255,193,7,0.08)',
                                            '&:hover': { bgcolor: 'rgba(255,193,7,0.12)' },
                                        },
                                    }}>
                                    <ListItemIcon
                                        sx={{ minWidth: 40, color: isActive ? BRAND_LUXE_GOLD : BRAND_LUXE_MUTED }}>
                                        <Icon sx={{ fontSize: 22 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={label}
                                        primaryTypographyProps={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 14,
                                            color: isActive ? BRAND_LUXE_GOLD : '#ffffff',
                                        }}
                                    />
                                </ListItemButton>
                            )}
                        </NavLink>
                    ))}
                </List>

                <Box sx={{ mt: 'auto' }}>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                    <List disablePadding>
                        <NavLink to={ROUTES.BRAND_PROFILE} style={{ textDecoration: 'none', color: 'inherit' }}>
                            {({ isActive }) => (
                                <ListItemButton
                                    selected={isActive}
                                    onClick={() => setDrawerOpen(false)}
                                    sx={{
                                        py: 1.5,
                                        px: 2.5,
                                        '&.Mui-selected': {
                                            bgcolor: 'rgba(255,193,7,0.08)',
                                            '&:hover': { bgcolor: 'rgba(255,193,7,0.12)' },
                                        },
                                    }}>
                                    <ListItemIcon
                                        sx={{ minWidth: 40, color: isActive ? BRAND_LUXE_GOLD : BRAND_LUXE_MUTED }}>
                                        <BusinessOutlinedIcon sx={{ fontSize: 22 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Brand profile"
                                        primaryTypographyProps={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 14,
                                            color: isActive ? BRAND_LUXE_GOLD : '#ffffff',
                                        }}
                                    />
                                </ListItemButton>
                            )}
                        </NavLink>
                        <NavLink to={ROUTES.BRAND_SETTINGS} style={{ textDecoration: 'none', color: 'inherit' }}>
                            {({ isActive }) => (
                                <ListItemButton
                                    selected={isActive}
                                    onClick={() => setDrawerOpen(false)}
                                    sx={{
                                        py: 1.5,
                                        px: 2.5,
                                        '&.Mui-selected': {
                                            bgcolor: 'rgba(255,193,7,0.08)',
                                            '&:hover': { bgcolor: 'rgba(255,193,7,0.12)' },
                                        },
                                    }}>
                                    <ListItemIcon
                                        sx={{ minWidth: 40, color: isActive ? BRAND_LUXE_GOLD : BRAND_LUXE_MUTED }}>
                                        <SettingsOutlinedIcon sx={{ fontSize: 22 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Settings"
                                        primaryTypographyProps={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 14,
                                            color: isActive ? BRAND_LUXE_GOLD : '#ffffff',
                                        }}
                                    />
                                </ListItemButton>
                            )}
                        </NavLink>
                        <ListItemButton
                            data-skip-brand-profile-gate="true"
                            onClick={() => {
                                setDrawerOpen(false);
                                dispatch(logout(undefined));
                            }}
                            sx={{ py: 1.5, px: 2.5, mb: 1 }}>
                            <ListItemIcon sx={{ minWidth: 40, color: '#f44336' }}>
                                <LogoutOutlinedIcon sx={{ fontSize: 22 }} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Sign out"
                                primaryTypographyProps={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 14,
                                    color: '#f44336',
                                }}
                            />
                        </ListItemButton>
                    </List>
                </Box>
            </Drawer>
        </Box>
    );
}
